/* =========================================================================
 * THE HAND-OFF LIST, ORDERED. Pure — no React, no DOM, no network.
 *
 * Brian, on the live Hand off stage, 2026-09-18: *"it is showing the old 12
 * builds - these arent sorted in anyway and it should be sorted by the rep- or
 * who made them - and it should show each of the 3 styles in this pack"*
 *
 * Three separate things in one sentence, and they are answered separately.
 *
 * ⚠️ 1. THERE WAS NO ORDER AT ALL. `StudioBuilds` rendered `res.builds` in
 * whatever order the index returned. Not newest-first, not by anything. A rep
 * opening this wants the thing they just made, so within a person the order is
 * most recent first, and the people are grouped so one rep's work is together.
 *
 * ⚠️ 2. CREATOR IS NOT REP, AND THEY ARE NOT CONFLATED HERE. A build's owner is
 * the signed-in user who made it — that is `creatorOf`. A REP is a different
 * fact, derived from ClickUp cross-referenced with Shopify, and it belongs to
 * the ACCOUNT, not to the build. This module reads a creator off the row and
 * says plainly when there is not one; it never guesses a rep from a build, and
 * it never presents one as the other.
 *
 * ⚠️ AND TODAY MOST ROWS HAVE NO CREATOR. Measured on the live index
 * 2026-09-18: the index rows carry championship, product, style, mode, status,
 * createdAt, buildId and beast, and NO author field of any spelling. So this is
 * a DATA GAP, not a display one: the backend has never captured who made a
 * build. `creatorOf` therefore returns `known: false` and the row says
 * "creator not recorded" rather than inventing "unknown user", because the two
 * read identically to a person and only one of them is true. The fix is a
 * creator field captured at create time, which is backend work.
 *
 * ⚠️ 3. "THE 3 STYLES IN THIS PACK" IS THE THREE LEVELS. There is no `pack`
 * concept anywhere in the data model — no field, no grouping, no route. What a
 * job actually has is up to three kinds of output: a Sourced build, a Generated
 * concept and a Bezel Drop. Those are `levels.js`, and a row already carries
 * the answer in `mode`, which was rendered as the bare word "generate" and read
 * as noise. So: LEVEL is labelled on every row from `mode`, and rows are
 * grouped by TEAM inside each person, so the three levels for one job sit
 * together. Grouping by team is a choice made because no pack field exists, and
 * it is stated rather than implied.
 * ========================================================================= */
import { levelForMode } from './levels.js'

const clean = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/**
 * WHO MADE THIS BUILD.
 *
 * Read as a candidate list rather than one spelling, the same way
 * `handoff.js#THUMB_KEYS` reads a preview: the index DTO is the backend's and
 * the field does not exist yet, so the day it ships under any of these names
 * the row shows it with no change here.
 *
 * `known: false` is the honest state and it is not the same as a blank name.
 */
const CREATOR_KEYS = [
  'createdBy', 'creator', 'createdByEmail', 'createdByName',
  'owner', 'ownerEmail', 'user', 'userEmail', 'by', 'author',
]

export function creatorOf(row) {
  for (const k of CREATOR_KEYS) {
    const v = row && row[k]
    if (v && typeof v === 'object') {
      const label = clean(v.name) || clean(v.email) || clean(v.id)
      if (label) return { id: clean(v.email) || label, label, known: true }
    }
    const s = clean(v)
    if (s) return { id: s, label: s, known: true }
  }
  return { id: '', label: 'Creator not recorded', known: false }
}

/**
 * WHICH JOB THIS BUILD IS FOR. The championship carries the team name on the
 * live index ("UIL (public) Golf Boys 4A · State · Texas"), so it is the job
 * key; a build with no championship attached groups under its own heading
 * rather than being silently merged into somebody else's team.
 */
const TEAM_KEYS = ['championshipName', 'schoolName', 'teamName', 'team']

/**
 * ⚠️ A RAW RECORD ID IS NOT A LABEL. Brian's phone screenshot, 2026-09-18: two
 * group headers read `57439867006` and `57426835533`. Those are HubSpot
 * championship ids and they mean nothing to a person.
 *
 * The fix was available on the row the whole time. The index carries `title`,
 * which is the job in words: "UIL (public) Golf Boys 4A · State · Texas". So
 * the id is used as the grouping KEY, because it is stable and two builds of
 * one championship must group together, and the TITLE is used as the label.
 *
 * When there is genuinely nothing but an id, the header says so in words
 * rather than printing the bare number and leaving the reader to guess what
 * kind of thing it is.
 */
export function teamOf(row) {
  const id = clean(row && row.championshipId) || clean(row && row.championshipKey)
  let label = ''
  for (const k of TEAM_KEYS) {
    const s = clean(row && row[k])
    if (s) { label = s; break }
  }
  if (!label) label = clean(row && row.title)

  if (id) {
    return {
      id,
      label: label || `Championship ${id}, no name on the record`,
      known: !!label,
      recordId: id,
    }
  }
  /* No championship attached. Group by the title so "ACC CHAMPIONS" and "2027
   * CONFERENCE" read as the separate jobs they are, instead of every
   * unattached build piling into one "No job attached" heap. */
  if (label) return { id: `title:${label.toLowerCase()}`, label, known: true, recordId: '' }
  return { id: '', label: 'No job attached', known: false, recordId: '' }
}

/** The LEVEL this build was made at, from the mode it was posted with. */
export function levelOf(row) {
  const l = levelForMode(clean(row && row.mode))
  return { id: l.id, n: l.n, label: l.label }
}

/**
 * ⚠️ A DATE CANNOT TELL TWO BUILDS APART THAT ARE 28 SECONDS APART.
 *
 * Brian's screenshot showed three rows reading "UIL (public) Golf Boys 4A ·
 * State · Texas", all generated, all draft, all Sep 18. They are three
 * DISTINCT records: 06:50:52.320, 06:51:00.833 and 06:51:20.716, ids ending
 * 0002n1xs, 0003i5u1 and 0004paq5. Not a query bug, not one build rendered
 * three times. Somebody pressed Render three times in 28 seconds, almost
 * certainly because nothing came back, which is the spend gate.
 *
 * So the row shows the TIME TO THE SECOND and the tail of the build id. The
 * time separates them for a reader; the id tail is the thing they can quote
 * back when two rows still look alike.
 */
export function stampOf(row, locale) {
  const raw = row && row.createdAt
  const d = new Date(raw)
  if (!raw || Number.isNaN(d.getTime())) return { when: '', id: shortId(row) }
  let when
  try {
    when = d.toLocaleString(locale || undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
    })
  } catch {
    when = d.toISOString()
  }
  return { when, id: shortId(row) }
}

/** The last chunk of a build id: `bld_20260918065120_0004paq5` -> `0004paq5`. */
export function shortId(row) {
  const id = clean(row && row.buildId)
  if (!id) return ''
  const tail = id.split('_').pop()
  return tail && tail !== id ? tail : id.slice(-8)
}

/**
 * Does this build actually carry artwork?
 *
 * ⚠️ EIGHT OF TWELVE ROWS ARE EMPTY, and the cause is not the list. Those
 * builds went through `POST /api/artwork/render` while `SR_ALLOW_IMAGE_SPEND`
 * is false, so the engine wrote the record, assembled every view's prompt and
 * skipped only the billable image call. They are SAVED, not failed. So an
 * empty build is drawn QUIETER rather than hidden, and the control that
 * filters them is visible with its own count. Hiding work somebody did,
 * silently, is how a rep concludes their build vanished.
 */
export function hasArt(row) {
  const n = row && (row.imageCount != null ? row.imageCount : (Array.isArray(row.images) ? row.images.length : 0))
  return Number(n) > 0
}

/* =====================================================================
 * SORTING AND FILTERING, AND THE TWO THINGS THAT MAKE THEM HONEST.
 *
 * ⚠️ 1. A DATE SORT MUST SAY WHICH DATE. A build has a createdAt and an
 * updatedAt and they are routinely days apart: the 2027 CONFERENCE builds were
 * created on 25 August and touched again later. "Sort by date" is therefore
 * ambiguous, and an ambiguous sort is one a person silently misreads. Every
 * option below names its field in words, and the UI prints that name.
 *
 * ⚠️ 2. A BLANK IS NOT A ZERO. Sorting by a missing date pushes those rows to
 * one end, where they read as "oldest" or "newest" rather than "we do not
 * know". They are separated out and COUNTED instead, so the list says "3 rows
 * have no date" rather than quietly implying one.
 *
 * The default is MOST RECENT ACTIVITY (updatedAt, falling back to createdAt),
 * because a rep opening Hand off is looking for what moved, not what was
 * created first.
 * ===================================================================== */

export const SORTS = Object.freeze([
  {
    id: 'activity',
    label: 'Most recent activity',
    field: 'updatedAt',
    note: 'when the build was last touched (updated, falling back to created)',
    get: (r) => stamp(r && (r.updatedAt || r.createdAt)),
  },
  {
    id: 'created-new',
    label: 'Newest first',
    field: 'createdAt',
    note: 'when the build was created',
    get: (r) => stamp(r && r.createdAt),
  },
  {
    id: 'created-old',
    label: 'Oldest first',
    field: 'createdAt',
    note: 'when the build was created',
    get: (r) => stamp(r && r.createdAt),
    asc: true,
  },
])

export const SORT_BY_ID = Object.fromEntries(SORTS.map((s) => [s.id, s]))
export const DEFAULT_SORT = 'activity'

function stamp(v) {
  if (v == null || v === '') return null
  const d = new Date(typeof v === 'number' ? v : String(v))
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

/** Ranges are measured against the SORT'S OWN field, never a different one. */
export const RANGES = Object.freeze([
  { id: 'all', label: 'Any time', days: 0 },
  { id: '24h', label: 'Last 24 hours', days: 1 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
])

export const RANGE_BY_ID = Object.fromEntries(RANGES.map((r) => [r.id, r]))

/**
 * Sort and filter, returning the kept rows AND the rows with no date.
 *
 * The undated rows are handed back separately rather than appended, so the
 * caller must decide what to say about them. That is deliberate: a function
 * that quietly tacks them on the end is how they end up misread as oldest.
 */
export function sortAndFilter(rows, { sort = DEFAULT_SORT, range = 'all', now = Date.now() } = {}) {
  const list = Array.isArray(rows) ? rows : []
  const s = SORT_BY_ID[sort] || SORT_BY_ID[DEFAULT_SORT]
  const r = RANGE_BY_ID[range] || RANGE_BY_ID.all

  const dated = []
  const undated = []
  for (const row of list) {
    const t = s.get(row)
    if (t == null) undated.push(row)
    else dated.push({ row, t })
  }

  let kept = dated
  let excluded = 0
  if (r.days > 0) {
    const cutoff = now - r.days * 86400000
    const before = kept.length
    kept = kept.filter((d) => d.t >= cutoff)
    excluded = before - kept.length
  }
  kept.sort((a, b) => (s.asc ? a.t - b.t : b.t - a.t))

  return {
    rows: kept.map((d) => d.row),
    undated,
    excluded,
    sort: s,
    range: r,
    /* The sentence the UI prints, so the wording cannot drift from the maths. */
    describe: `sorted by ${s.label.toLowerCase()}, using ${s.note}`,
  }
}

/**
 * The whole list, grouped and ordered:
 *
 *   creator (known first, A→Z; "not recorded" last)
 *     └ team (most recently touched team first)
 *         └ build, NEWEST FIRST
 *
 * Known creators sort before the unrecorded ones because a list whose first
 * heading is "Creator not recorded" reads as broken, and once the backend
 * captures a creator that heading shrinks to nothing on its own.
 */
export function groupBuilds(rows, creators) {
  const list = Array.isArray(rows) ? rows : []
  /* ⚠️ THE INCOMING ORDER IS THE CHOSEN ORDER. `rows` arrives from
   * sortAndFilter, already in the order the person picked in the sort control.
   * Re-sorting by createdAt inside here would silently overrule that control
   * and make "Most recent activity" a lie on a grouped list. So position in
   * `list` is the rank, and every sort below uses it. */
  const rank = new Map(list.map((r, i) => [r.buildId, i]))
  const at = (r) => (rank.has(r && r.buildId) ? rank.get(r.buildId) : Number.MAX_SAFE_INTEGER)
  const byCreator = new Map()
  /* ⚠️ CREATOR IS ON THE RECORD, JUST NOT ON THE INDEX, and I reported the
   * opposite earlier. The full record carries
   * `createdBy: {id:"brian", name:"Brian Kass"}` on builds going back to 25
   * August; `GET /api/artwork/builds` simply does not return it. So no backend
   * work is needed to CAPTURE a creator, only to expose it, and until that
   * happens the client hydrates it from the records it is already opening for
   * thumbnails. `creators` is that map, buildId -> row-shaped fragment. */
  const hydrated = creators instanceof Map ? creators : new Map(Object.entries(creators || {}))

  for (const row of list) {
    /* ⚠️ HYDRATION FILLS A GAP. IT DOES NOT OVERWRITE A FACT.
     *
     * This read `creatorOf(hydrated.get(id) || row)`, which prefers the
     * hydrated fragment whenever one EXISTS — and the hydrating effect stores
     * `{ createdBy: rec.createdBy || '' }`, so a record read that comes back
     * without a creator writes an empty fragment that then REPLACES a creator
     * the index did supply. Every row rendered "Creator not recorded" in the
     * walkthrough with `createdBy` present on all six.
     *
     * In production this is currently masked, because the index omits the
     * field and there is nothing to destroy. It would have surfaced as a
     * regression on the day the index starts returning it — which is the
     * stated plan — and it would have looked like the backend had broken.
     *
     * So the row wins when it knows, and hydration is consulted only when it
     * does not. Same rule as propose-dont-overwrite: a fallback may not
     * silently beat real data. */
    const own = creatorOf(row)
    const c = own.known ? own : creatorOf(hydrated.get(row.buildId) || row)
    const t = teamOf(row)
    const key = c.known ? c.id : ' unrecorded'
    if (!byCreator.has(key)) byCreator.set(key, { creator: c, teams: new Map() })
    const g = byCreator.get(key)
    if (!g.teams.has(t.id)) g.teams.set(t.id, { team: t, builds: [] })
    g.teams.get(t.id).builds.push({ ...row, level: levelOf(row), creator: c, team: t })
  }

  const groups = [...byCreator.values()].map((g) => {
    const teams = [...g.teams.values()].map((t) => ({
      ...t,
      builds: t.builds.sort((a, b) => at(a) - at(b)),
      /* Which levels this job actually has, so a job missing one is visible as
       * a gap rather than as an absence nobody notices. */
      levels: [...new Set(t.builds.map((b) => b.level.id))],
    }))
    teams.sort((a, b) => at(a.builds[0]) - at(b.builds[0]))
    return { ...g, teams, count: teams.reduce((n, t) => n + t.builds.length, 0) }
  })

  groups.sort((a, b) => {
    if (a.creator.known !== b.creator.known) return a.creator.known ? -1 : 1
    return a.creator.label.localeCompare(b.creator.label)
  })
  return groups
}

/** How many of the list carry no creator — the size of the data gap, for the UI. */
export function unrecordedCreators(rows) {
  return (Array.isArray(rows) ? rows : []).filter((r) => !creatorOf(r).known).length
}
